/**
 * The occupancy grid. Shared by world generation, player collision, entity
 * navigation and mesh building, so it lives in core rather than in world/.
 */

import { Cell, type Cell2, type Grid, type Vec2 } from './types';

class GridImpl implements Grid {
  readonly w: number;
  readonly h: number;
  readonly cellSize: number;
  readonly cells: Uint8Array;

  constructor(w: number, h: number, cellSize: number, fill: Cell = Cell.Void) {
    this.w = w;
    this.h = h;
    this.cellSize = cellSize;
    this.cells = new Uint8Array(w * h);
    if (fill !== Cell.Void) this.cells.fill(fill);
  }

  at(cx: number, cy: number): Cell {
    if (cx < 0 || cy < 0 || cx >= this.w || cy >= this.h) return Cell.Void;
    return this.cells[cy * this.w + cx] as Cell;
  }

  set(cx: number, cy: number, v: Cell): void {
    if (cx < 0 || cy < 0 || cx >= this.w || cy >= this.h) return;
    this.cells[cy * this.w + cx] = v;
  }

  inBounds(cx: number, cy: number): boolean {
    return cx >= 0 && cy >= 0 && cx < this.w && cy < this.h;
  }

  isSolid(cx: number, cy: number): boolean {
    const c = this.at(cx, cy);
    return c === Cell.Void || c === Cell.Wall;
  }

  isWalkable(cx: number, cy: number): boolean {
    const c = this.at(cx, cy);
    return c !== Cell.Void && c !== Cell.Wall;
  }

  worldToCell(wx: number, wz: number): Cell2 {
    return { cx: Math.floor(wx / this.cellSize), cy: Math.floor(wz / this.cellSize) };
  }

  cellToWorld(cx: number, cy: number): Vec2 {
    return { x: (cx + 0.5) * this.cellSize, z: (cy + 0.5) * this.cellSize };
  }

  isSolidAt(wx: number, wz: number): boolean {
    return this.isSolid(Math.floor(wx / this.cellSize), Math.floor(wz / this.cellSize));
  }
}

export function makeGrid(w: number, h: number, cellSize: number, fill: Cell = Cell.Void): Grid {
  return new GridImpl(w, h, cellSize, fill);
}

/** Unreachable marker used by `bfsDistances`. */
export const UNREACHABLE = 0xffff;

/**
 * Breadth-first distance in cells from one or more sources over walkable cells.
 * Returns a row-major Uint16Array matching `grid.cells`.
 */
export function bfsDistances(grid: Grid, sources: readonly Cell2[]): Uint16Array {
  const { w, h } = grid;
  const dist = new Uint16Array(w * h).fill(UNREACHABLE);
  const queue = new Int32Array(w * h);
  let head = 0;
  let tail = 0;

  for (const s of sources) {
    if (!grid.inBounds(s.cx, s.cy) || !grid.isWalkable(s.cx, s.cy)) continue;
    const idx = s.cy * w + s.cx;
    if (dist[idx] === 0) continue;
    dist[idx] = 0;
    queue[tail++] = idx;
  }

  while (head < tail) {
    const idx = queue[head++]!;
    const cx = idx % w;
    const cy = (idx / w) | 0;
    const d = dist[idx]! + 1;
    // 4-connected: entities and the player move through orthogonal gaps.
    for (let i = 0; i < 4; i++) {
      const nx = cx + (i === 0 ? 1 : i === 1 ? -1 : 0);
      const ny = cy + (i === 2 ? 1 : i === 3 ? -1 : 0);
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const nIdx = ny * w + nx;
      if (dist[nIdx] !== UNREACHABLE) continue;
      if (!grid.isWalkable(nx, ny)) continue;
      dist[nIdx] = d;
      queue[tail++] = nIdx;
    }
  }
  return dist;
}

/** Every walkable cell reachable from `start`, as flat indices. */
export function floodFill(grid: Grid, start: Cell2): Int32Array {
  const dist = bfsDistances(grid, [start]);
  const out: number[] = [];
  for (let i = 0; i < dist.length; i++) if (dist[i] !== UNREACHABLE) out.push(i);
  return Int32Array.from(out);
}

/**
 * Bresenham line-of-sight between two world points. Returns true when nothing
 * solid blocks the segment. Used for entity sight and light estimation.
 */
export function hasLineOfSight(grid: Grid, a: Vec2, b: Vec2): boolean {
  const cs = grid.cellSize;
  let x0 = Math.floor(a.x / cs);
  let y0 = Math.floor(a.z / cs);
  const x1 = Math.floor(b.x / cs);
  const y1 = Math.floor(b.z / cs);

  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  // Bound the walk so a bad call can never hang the frame.
  let guard = dx + dy + 2;

  while (guard-- > 0) {
    if (x0 === x1 && y0 === y1) return true;
    if (grid.isSolid(x0, y0) && !(x0 === Math.floor(a.x / cs) && y0 === Math.floor(a.z / cs))) return false;
    const e2 = err * 2;
    const stepX = e2 > -dy;
    const stepY = e2 < dx;
    // On an exact diagonal both axes advance in one iteration, which walks
    // through the corner between two cells without ever testing either of them.
    // Collision and A* both refuse that pinch, so sight must too, or an entity
    // can see the player through a gap neither of them can move through.
    if (stepX && stepY && grid.isSolid(x0 + sx, y0) && grid.isSolid(x0, y0 + sy)) return false;
    if (stepX) {
      err -= dy;
      x0 += sx;
    }
    if (stepY) {
      err += dx;
      y0 += sy;
    }
  }
  return false;
}

/** Convert a world position to the flat cell index, or -1 when out of bounds. */
export function indexAt(grid: Grid, pos: Vec2): number {
  const cx = Math.floor(pos.x / grid.cellSize);
  const cy = Math.floor(pos.z / grid.cellSize);
  if (!grid.inBounds(cx, cy)) return -1;
  return cy * grid.w + cx;
}

/** Iterate the 8 neighbours of a cell. */
export const NEIGHBORS_8: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

export const NEIGHBORS_4: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
